import { useRestartPreview, useSourcingRun, useStopSourcing } from "@vidario/api-client";
import { restartDeletionSummary, searchRestartPlan } from "@vidario/domain";
import { Button, Modal, useToast } from "@vidario/ui";
import { AlertTriangle } from "lucide-react";
import { useRef, type ReactElement } from "react";

export interface RestartSourcingDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading?: boolean;
  /** When set, offers a "Parameter anpassen…" action that takes the admin
   * to the edit screen to change the search parameters before re-running.
   * Omit to hide it (e.g. a plain same-parameters restart). */
  onAdjustParams?: () => void;
}

/** Warn-before-rerun confirm for the admin's Search restart. It deletes every
 * not-selected candidate and triggers new (paid) API calls, so a stray click
 * must not be enough. While the job's search is running it can't restart at
 * all (#18). */
export function RestartSourcingDialog({
  jobId,
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  onAdjustParams,
}: RestartSourcingDialogProps): ReactElement {
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const stop = useStopSourcing();
  const runQuery = useSourcingRun(jobId, { enabled: open });
  const preview = useRestartPreview(jobId, { enabled: open });
  const plan = searchRestartPlan(
    // Unknown until the query settles; if it fails, the backend still refuses
    // a restart during a search, so don't keep the dialog waiting forever.
    runQuery.isSuccess ? (runQuery.data?.status ?? null) : runQuery.isError ? null : undefined,
    // The dialog stays mounted between openings: don't let last time's counts
    // or run status stand in for the current ones.
    {
      refreshing: runQuery.isFetching || preview.isFetching,
      stopRequested: Boolean(runQuery.data?.stopRequestedAt),
    },
  );

  if (plan.blocked) {
    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="Kandidatensuche kann gerade nicht neu gestartet werden"
        size="sm"
        footer={
          <>
            {!plan.stopping && (
              <Button
                variant="secondary"
                loading={stop.isPending}
                onClick={() =>
                  stop.mutate(jobId, {
                    onSuccess: () => {
                      onOpenChange(false);
                      toast.push({
                        kind: "success",
                        title: "Suche wird gestoppt",
                        message: "Danach kannst du sie neu starten.",
                      });
                    },
                    onError: (err) =>
                      toast.push({
                        kind: "error",
                        title: "Stoppen fehlgeschlagen",
                        message: err.message,
                      }),
                  })
                }
              >
                Suche stoppen
              </Button>
            )}
            <Button onClick={() => onOpenChange(false)}>OK</Button>
          </>
        }
      >
        <p className="text-sm text-text-primary">
          Die Kandidatensuche läuft noch. Ein Neustart ist erst möglich, wenn sie fertig ist.
        </p>
        <p className="mt-2 text-sm text-text-secondary">
          {plan.stopping
            ? "Sie wird gerade gestoppt; danach kannst du sie neu starten."
            : "Du kannst sie stoppen; bereits gefundene Kandidaten bleiben erhalten."}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Kandidatensuche neu starten?"
      size="md"
      // A reflex Enter keeps the candidates (#186).
      initialFocusRef={cancelRef}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            loading={loading || plan.checking || preview.isPending}
            onClick={onConfirm}
            data-testid="confirm-restart-sourcing"
          >
            Ja, neu starten
          </Button>
        </>
      }
    >
      <div className="flex gap-2 rounded-md bg-danger-50 p-3 text-sm text-danger-700">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">
            Achtung: Alle nicht ausgewählten Talente werden dadurch gelöscht.
          </div>
          <div className="mt-1 text-xs">
            {preview.data && !preview.isFetching && `${restartDeletionSummary(preview.data)} `}
            Ausgewählte Talente bleiben erhalten. Außerdem entstehen erneut API-Kosten für die
            Suche.
          </div>
        </div>
      </div>
      {onAdjustParams && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-border-default p-3 text-sm">
          <span className="text-text-secondary">Lieber erst die Suchparameter anpassen?</span>
          <Button variant="secondary" size="sm" onClick={onAdjustParams}>
            Parameter anpassen…
          </Button>
        </div>
      )}
    </Modal>
  );
}
